// Profile switcher bar: presentational DOM only.
//
// Renders a tab per profile with a power control and a stale-fork badge, and
// reports user intent via callbacks. It never calls the profiles API itself
// (see profiles.ts) -- wiring that up, and reattaching the terminal on
// selection, is Task 18's job.

import { profileStatusLabel, type Profile } from './profiles';

export interface ProfileBarOptions {
  container: HTMLElement;
  profiles: Profile[];
  activeName: string | null;
  onSelect: (name: string) => void;
  onStart: (name: string) => void;
  onStop: (name: string) => void;
  onManage: () => void;
}

export function renderProfileBar(options: ProfileBarOptions): HTMLElement {
  const { container, profiles, activeName } = options;

  // Idempotent: a re-render (poll/start/stop) replaces the previous bar
  // rather than appending a second one.
  container.querySelector('.profile-bar')?.remove();

  const bar = document.createElement('nav');
  bar.className = 'profile-bar';
  bar.setAttribute('aria-label', 'Profiles');

  for (const p of profiles) {
    bar.appendChild(renderTab(p, p.name === activeName, options));
  }

  const manage = document.createElement('button');
  manage.className = 'profile-manage';
  manage.type = 'button';
  manage.textContent = '+';
  manage.title = 'Manage profiles';
  manage.addEventListener('click', () => options.onManage());
  bar.appendChild(manage);

  container.prepend(bar);
  return bar;
}

// display_name and last_error are both unvalidated user input (last_error is
// raw, unfiltered process stderr -- no length limit, no character
// restriction), and both end up in a `title` tooltip below. Unlike files.ts
// (which builds markup as template-literal strings later parsed via
// .innerHTML, where an unescaped '"' or '&' really could break out of an
// attribute), every tooltip here is set with a plain DOM property assignment
// (`el.title = ...`) on an element built with createElement. A property
// assignment is never parsed as HTML, so there is no attribute-value context
// to break out of in the first place -- not for '<'/'>' (confirmed by
// round-tripping a raw `<img src=x onerror=...>` display_name through
// innerHTML: it reparses to one text node and zero <img> elements, because
// it never leaves the quoted attribute value the serializer already placed
// it in) and not for '"'/'&' either (the one thing that could matter for a
// literal round-trip -- serializing this element back to markup via
// .outerHTML/.innerHTML -- is handled by the serializer itself, which
// escapes '&' and the quote delimiter on the way out; escaping them
// ourselves first just means the serializer escapes our escaping too,
// turning an ordinary '&' in a display name into a literal "&amp;" in the
// live tooltip today and "&amp;amp;" on any later round-trip). So: nothing
// here needs escaping, and escaping it was actively wrong. Values placed via
// textContent (the visible label) were never at any risk either --
// text-node serialization escapes on its own, and textContent never parses
// its input as markup.

function renderTab(p: Profile, isActive: boolean, options: ProfileBarOptions): HTMLButtonElement {
  const tab = document.createElement('button');
  tab.className = 'profile-tab';
  tab.type = 'button';
  tab.dataset.profile = p.name;
  tab.dataset.state = p.state;
  if (isActive) tab.setAttribute('aria-current', 'true');

  // name is the server-validated slug; it disambiguates two profiles that
  // happen to share a display name. display_name is the user-chosen label
  // and belongs in the tooltip for the same reason it belongs in the visible
  // tab: it's what the user actually named the thing.
  const titleLines = [`${p.display_name} (${p.name}) - ${profileStatusLabel(p)}`];
  if (p.state === 'failed' && p.last_error) titleLines.push(p.last_error);
  tab.title = titleLines.join('\n');

  const label = document.createElement('span');
  label.className = 'profile-label';
  label.textContent = p.display_name;
  tab.appendChild(label);

  if (p.forked && p.behind_by > 0) {
    const badge = document.createElement('span');
    badge.className = 'profile-badge';
    badge.textContent = '!';
    badge.title = `The ${p.base} base is ${p.behind_by} version(s) ahead of your fork`;
    tab.appendChild(badge);
  }

  const power = document.createElement('span');
  power.className = 'profile-power';
  power.setAttribute('role', 'button');
  power.textContent = p.state === 'running' ? '■' : '▶';
  power.title = p.state === 'running' ? `Stop ${p.display_name}` : `Start ${p.display_name}`;
  power.addEventListener('click', (e) => {
    // The power control sits inside the clickable tab; it must not also
    // trigger onSelect.
    e.stopPropagation();
    if (p.state === 'running') options.onStop(p.name);
    else options.onStart(p.name);
  });
  tab.appendChild(power);

  tab.addEventListener('click', () => options.onSelect(p.name));
  return tab;
}

const PROFILE_BAR_CSS = `
.profile-bar {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: var(--header-bg);
  border-bottom: 1px solid var(--border-color);
  overflow-x: auto;
}

.profile-tab {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 10px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: transparent;
  color: var(--text-color);
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}

.profile-tab::before {
  content: '';
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #808080;
  flex-shrink: 0;
}

.profile-tab[data-state="running"]::before {
  background: var(--accent-color);
}

.profile-tab[data-state="failed"]::before {
  background: var(--error-color);
}

.profile-tab[data-state="unknown"]::before {
  background: #cd9b00;
}

.profile-tab[aria-current="true"] {
  border-color: var(--accent-color);
  background: rgba(0, 205, 0, 0.1);
}

.profile-label {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  border-radius: 7px;
  background: #cd9b00;
  color: #0a0a0a;
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
}

.profile-power {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  color: var(--text-color);
  cursor: pointer;
}

.profile-power:hover {
  background: rgba(255, 255, 255, 0.1);
}

.profile-manage {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: transparent;
  color: var(--text-color);
  font-size: 14px;
  cursor: pointer;
  flex-shrink: 0;
}

.profile-manage:hover {
  background: rgba(255, 255, 255, 0.1);
}

@media (max-width: 768px) {
  .profile-bar {
    padding: 4px 6px;
  }
  .profile-label {
    max-width: 96px;
  }
}
`;

let stylesInjected = false;

export function injectProfileBarStyles(): void {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.textContent = PROFILE_BAR_CSS;
  document.head.appendChild(style);
}
